import { Controller } from '@hotwired/stimulus'

// Every preview that asks to be measured waits for one shared frame. Each
// check reads layout and then writes classes and heights, so checking a long
// timeline's messages one after another made the browser lay the whole page
// out again for each of them. The frame reads every preview first and writes
// after, so the page is laid out once for the reads and once for the cut.
const pending = new Set()
let frame = null
let timer = null

function queueCheck(controller) {
  pending.add(controller)
  if (document.hidden) {
    // A frame queued while visible pauses once the page hides: use a timer.
    if (frame != null) cancelAnimationFrame(frame)
    frame = null
    timer ??= setTimeout(flushChecks, 0)
  } else if (frame == null && timer == null) {
    frame = requestAnimationFrame(flushChecks)
  }
}

function dequeueCheck(controller) {
  pending.delete(controller)
  if (pending.size) return
  if (frame != null) cancelAnimationFrame(frame)
  if (timer != null) clearTimeout(timer)
  frame = null
  timer = null
}

function flushChecks() {
  frame = null
  timer = null
  const batch = [...pending].filter(controller => controller.takeCheck())
  pending.clear()

  // A viewport-fitted preview reads its own top, which moves as the previews
  // above it collapse, so it is measured after them, alone.
  const plain = batch.filter(controller => !controller.fitViewportValue)
  const fitted = batch.filter(controller => controller.fitViewportValue)
  const heights = plain.map(controller => controller.contentElement.scrollHeight)
  plain.forEach((controller, index) => controller.showCollapsed(heights[index] > controller.maxHeightValue))
  const collapsing = plain.filter(controller => controller.isExpandable)
  const cuts = collapsing.map(controller => controller.measureCollapse(controller.maxHeightValue))
  collapsing.forEach((controller, index) => controller.finishCollapse(controller.maxHeightValue, cuts[index]))
  plain.filter(controller => !controller.isExpandable).forEach(controller => controller.finishOpen())
  fitted.forEach(controller => controller.checkHeight())
}

export default class extends Controller {
  static values = {
    maxHeight: { type: Number, default: 200 },
    expandOnContainerClick: { type: Boolean, default: false },
    // When true, the collapsed preview is capped to fit the current viewport
    // (clamped between minHeight and maxHeight) so whatever sits below the
    // collapsible — e.g. the task outcome card — stays at least partially on
    // screen instead of being pushed below the fold. Off by default so other
    // consumers keep their fixed maxHeight behavior.
    fitViewport: { type: Boolean, default: false },
    // Pixels reserved between the collapsed preview's bottom and the bottom of
    // the viewport so the following content peeks into view.
    viewportReserve: { type: Number, default: 150 },
    // Floor for the viewport-fit height so the preview stays useful on short
    // screens even if that means the trailing content scrolls partly off.
    minHeight: { type: Number, default: 240 }
  }

  connect() {
    this.boundToggle = this.toggle.bind(this)
    this.boundContainerClick = null
    this.boundResize = null
    this.resizeObserver = null
    this.checkScheduled = false
    this.initialCheckPending = true
    this.boundVisibilityChange = this.handleVisibilityChange.bind(this)
    document.addEventListener('visibilitychange', this.boundVisibilityChange)

    this.contentElement = this.element.querySelector('[data-expandable-content-target="content"], .expandable-content')
    this.buttonElement = this.element.querySelector('[data-expandable-content-target="button"], .read-more-btn')
    this.buttonTextElement = this.element.querySelector('[data-expandable-content-target="buttonText"], .read-more-btn span')
    this.iconElement = this.element.querySelector('[data-expandable-content-target="icon"], .read-more-btn i')
    this.isExpandable = false

    if (this.buttonElement) {
      this.stripStimulusAction(this.buttonElement)
      this.ensureButtonParts()
      this.collapsedLabel = this.buttonTextElement?.textContent?.trim() || 'Read more'
      this.expandedLabel = this.buttonElement.dataset.expandedLabel?.trim() || 'Read less'
      this.buttonElement.addEventListener('click', this.boundToggle)
      this.buttonElement.setAttribute('aria-expanded', 'false')
    }

    if (this.expandOnContainerClickValue) {
      this.boundContainerClick = this.handleContainerClick.bind(this)
      this.element.addEventListener('click', this.boundContainerClick)
    }

    if (this.fitViewportValue) {
      this.boundResize = this.scheduleCheck.bind(this)
      window.addEventListener('resize', this.boundResize, { passive: true })
    }

    // One measurement at connect is not enough. Content measured before the
    // page settles — while its column is still narrow, or before a lazy frame
    // above it loads and the layout shifts — can read as far taller than it
    // is, and a one-line message then stayed faded behind "Read more" for
    // good. Measure again whenever the content's box changes size. A box that
    // only moves (a lazy frame above it loads while its own size stays put)
    // is not observed: ResizeObserver reports size, not position. Under
    // fitViewport the window resize listener above covers the viewport case.
    if (this.contentElement && typeof ResizeObserver !== 'undefined') {
      this.resizeObserver = new ResizeObserver(() => this.scheduleCheck())
      this.resizeObserver.observe(this.contentElement)
    }

    this.scheduleCheck()
  }

  disconnect() {
    this.cancelScheduledCheck()
    document.removeEventListener('visibilitychange', this.boundVisibilityChange)

    if (this.buttonElement) {
      this.buttonElement.removeEventListener('click', this.boundToggle)
    }

    if (this.boundContainerClick) {
      this.element.removeEventListener('click', this.boundContainerClick)
      this.boundContainerClick = null
    }

    if (this.boundResize) {
      window.removeEventListener('resize', this.boundResize)
      this.boundResize = null
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
  }

  ensureButtonParts() {
    if (!this.buttonElement) return

    const initialLabel = (this.buttonTextElement?.textContent || this.buttonElement.textContent || '').trim() || 'Read more'

    this.buttonElement.innerHTML = ''

    const label = document.createElement('span')
    label.textContent = initialLabel
    label.dataset.expandableContentTarget = 'buttonText'
    this.buttonElement.appendChild(label)
    this.buttonTextElement = label

    const icon = document.createElement('i')
    icon.className = 'ti ti-chevron-down'
    icon.setAttribute('aria-hidden', 'true')
    icon.dataset.expandableContentTarget = 'icon'
    this.buttonElement.appendChild(icon)
    this.iconElement = icon
  }

  isCollapsed() {
    return this.contentElement?.classList.contains('collapsed')
  }

  stripStimulusAction(element) {
    const actionAttr = element.getAttribute('data-action')
    if (!actionAttr) return

    const updated = actionAttr
      .split(' ')
      .filter(action => !action.includes('expandable-content#toggle'))
      .join(' ')

    if (updated) {
      element.setAttribute('data-action', updated)
    } else {
      element.removeAttribute('data-action')
    }
  }

  // Effective collapsed height. When fitViewport is on, shrink the fixed
  // maxHeight to whatever the viewport can spare below the preview's top edge,
  // clamped to [minHeight, maxHeight], so trailing content stays in view.
  collapsedMaxHeight() {
    const base = this.maxHeightValue
    if (!this.fitViewportValue || !this.contentElement) return base

    const top = this.contentElement.getBoundingClientRect().top
    const available = window.innerHeight - top - this.viewportReserveValue
    return Math.max(this.minHeightValue, Math.min(base, Math.round(available)))
  }

  applyCollapsedHeight(maxHeight) {
    if (!this.contentElement) return
    this.applyCollapse(maxHeight, this.measureCollapse(maxHeight))
  }

  // Reads only: the backdrop the fade dissolves into, and where the cut lands.
  measureCollapse(maxHeight) {
    const content = this.contentElement
    return { surface: this.backdropColor(content.parentElement), clean: this.cleanBreak(maxHeight) }
  }

  // Writes only, and none that would change nothing: an unchanged page then
  // needs no new layout when the next preview is read.
  applyCollapse(maxHeight, { surface, clean }) {
    const content = this.contentElement
    if (surface && content.style.getPropertyValue('--fade-surface') !== surface) content.style.setProperty('--fade-surface', surface)
    // Stop at the end of the last paragraph, table row or list item that fits,
    // so no line is cut in half behind the fade.
    content.classList.toggle('collapsed--clean', Boolean(clean))
    const height = clean ? `${clean}px` : (this.fitViewportValue ? `${maxHeight}px` : '')
    if (content.style.maxHeight !== height) content.style.maxHeight = height
  }

  clearCollapsedHeight() {
    if (!this.contentElement) return
    this.contentElement.style.removeProperty('--fade-surface')
    this.contentElement.classList.remove('collapsed--clean')
    this.contentElement.style.maxHeight = ''
  }

  // The bottom of the last whole block above maxHeight (a whole table before
  // one of its rows), or null when the nearest would leave less than half the
  // preview. A heading or a rule is never the last thing shown: it introduces
  // what the cut would hide.
  cleanBreak(maxHeight) {
    const content = this.contentElement
    const top = content.getBoundingClientRect().top
    const lowest = (selector) => {
      let best = null
      content.querySelectorAll(selector).forEach((block) => {
        const bottom = block.getBoundingClientRect().bottom - top
        if (bottom <= maxHeight - 4 && (best === null || bottom > best)) best = bottom
      })
      return best !== null && best >= maxHeight * 0.5 ? Math.ceil(best) + 4 : null
    }
    return lowest('p, li, blockquote, pre, table') || lowest('tr')
  }

  // The collapsed fade must dissolve into whatever the preview actually sits on.
  // A per-surface CSS list (`--fade-surface`) drifts out of date every time a
  // card, drawer or tinted wash changes its fill, and the stale colour paints a
  // lighter or darker band over the last lines. Read the real backdrop instead:
  // composite the translucent fills from the nearest opaque ancestor down to the
  // content's parent, and hand that to the fade. The CSS value stays as the
  // fallback when nothing can be measured.
  backdropColor(start) {
    const layers = []
    for (let node = start; node && node.nodeType === 1; node = node.parentElement) {
      const color = getComputedStyle(node).backgroundColor
      if (!color || color === 'transparent' || color === 'rgba(0, 0, 0, 0)') continue
      layers.push(color)
      if (this.isOpaque(color)) break
    }
    if (layers.length === 0) return null

    const canvas = (this.constructor.fadeCanvas ||= document.createElement('canvas'))
    canvas.width = canvas.height = 1
    const ctx = canvas.getContext('2d', { willReadFrequently: true })
    if (!ctx) return null
    ctx.clearRect(0, 0, 1, 1)
    // Paint outermost first so each translucent fill lands over the one behind.
    for (const color of layers.reverse()) {
      ctx.fillStyle = '#000'
      ctx.fillStyle = color
      ctx.fillRect(0, 0, 1, 1)
    }
    const [r, g, b, a] = ctx.getImageData(0, 0, 1, 1).data
    // Nothing opaque above the content: let the stylesheet's colour stand
    // rather than fading to a half-transparent guess.
    return a === 255 ? `rgb(${r} ${g} ${b})` : null
  }

  isOpaque(color) {
    const alpha = color.match(/\/\s*([\d.]+%?)\s*\)$/) || color.match(/^rgba\([^,]+,[^,]+,[^,]+,\s*([\d.]+)\s*\)$/)
    if (!alpha) return true
    const value = alpha[1].endsWith('%') ? parseFloat(alpha[1]) / 100 : parseFloat(alpha[1])
    return value >= 1
  }

  checkHeight() {
    const content = this.contentElement
    if (!content) return

    const maxHeight = this.collapsedMaxHeight()
    this.showCollapsed(content.scrollHeight > maxHeight)
    if (this.isExpandable) this.finishCollapse(maxHeight, this.measureCollapse(maxHeight))
    else this.finishOpen()
  }

  // The class and button writes a measured height decides, before the cut is
  // measured (a collapsed box can place its first block differently).
  showCollapsed(isExpandable) {
    const content = this.contentElement
    this.isExpandable = isExpandable
    this.setButtonVisible(this.buttonElement, isExpandable)
    if (isExpandable) content.classList.add('collapsed')
    else content.classList.remove('collapsed')
    content.classList.remove('expanded')
  }

  finishCollapse(maxHeight, cut) {
    this.applyCollapse(maxHeight, cut)
    this.updateButtonState(false)
    this.updateContainerState(false)
  }

  finishOpen() {
    this.clearCollapsedHeight()
    this.updateButtonState(false)
    this.updateContainerState(true)
  }

  setButtonVisible(button, isVisible) {
    if (!button) return

    if (button.hidden !== !isVisible) button.hidden = !isVisible
    button.classList.toggle('hidden', !isVisible)
  }

  toggle(event) {
    if (event) event.preventDefault()

    if (!this.contentElement) return
    if (!this.isExpandable) return

    if (this.isCollapsed()) {
      this.expand()
    } else {
      this.collapse()
    }
  }

  expand() {
    const content = this.contentElement
    if (!content) return

    content.classList.remove('collapsed')
    content.classList.add('expanded')
    this.clearCollapsedHeight()
    this.updateButtonState(true)
    this.updateContainerState(true)
  }

  collapse() {
    const content = this.contentElement
    if (!content) return

    content.classList.add('collapsed')
    content.classList.remove('expanded')
    this.applyCollapsedHeight(this.collapsedMaxHeight())
    this.updateButtonState(false)
    this.updateContainerState(false)
  }

  updateButtonState(isExpanded) {
    if (this.buttonElement) {
      this.buttonElement.classList.toggle('is-expanded', isExpanded)
      this.buttonElement.setAttribute('aria-expanded', String(isExpanded))
    }

    if (this.buttonTextElement) {
      const label = isExpanded ? (this.expandedLabel || 'Read less') : (this.collapsedLabel || 'Read more')
      if (this.buttonTextElement.textContent !== label) this.buttonTextElement.textContent = label
    }

    if (this.iconElement) {
      this.iconElement.classList.add('ti')
      this.iconElement.classList.toggle('ti-chevron-down', !isExpanded)
      this.iconElement.classList.toggle('ti-chevron-up', isExpanded)
    }
  }

  updateContainerState(isExpanded) {
    if (!this.expandOnContainerClickValue) return

    this.element.classList.toggle('expandable-content-container--expanded', isExpanded)
    this.element.classList.toggle('expandable-content-container--collapsed', !isExpanded)
  }

  handleContainerClick(event) {
    if (!this.expandOnContainerClickValue) return
    if (!this.isExpandable) return
    if (!this.isCollapsed()) return

    if (this.buttonElement && this.buttonElement.contains(event.target)) return
    if (event.target.closest('a, button, input, textarea, select, label')) return

    this.expand()
  }

  // A frame queued while visible pauses if the page hides before it runs.
  // Transfer that pending measurement to the hidden-page timer.
  handleVisibilityChange() {
    if (!document.hidden || !this.checkScheduled) return
    this.cancelScheduledCheck()
    this.scheduleCheck()
  }

  cancelScheduledCheck() {
    dequeueCheck(this)
    this.checkScheduled = false
  }

  // Measure again on the next frame, once however many size changes arrive —
  // a new viewport for a fitted preview, or the content's own box settling —
  // but never re-collapse a preview the user has already expanded. A hidden
  // page (a background tab) gets no frames, so it measures on a timer instead;
  // its layout is still real, and a check left waiting for a frame would also
  // swallow every size change after it.
  scheduleCheck() {
    if (this.checkScheduled) return
    this.checkScheduled = true
    queueCheck(this)
  }

  // Called by the shared frame: whether this preview still wants measuring.
  // Rebuild state on connect; later checks preserve the reader's choice.
  takeCheck() {
    this.checkScheduled = false
    const initialCheck = this.initialCheckPending
    this.initialCheckPending = false
    if (!this.contentElement) return false
    return initialCheck || !this.contentElement.classList.contains('expanded')
  }
}
